import React, { useCallback, useState } from 'react';

import { Permissions } from '@fragment/authorization-utils/permissions';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { LinkIcon } from '@fragment/ui/src/components/Icon/LinkIcon/LinkIcon';
import { Menu } from '@fragment/ui/src/components/Menu/Menu';

import type { LinkType } from '../../link/types';
import { useGetLinksQuery } from '../../../hooks/customerApi';
import { useCustomerApiContext } from '../../../hooks/useCustomerApiContext';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useHasWorkspacePermission } from '../../../hooks/useHasWorkspacePermission';
import { getResultOrError } from '../../../utils/errors';
import { LinksGrid } from '../../link/LinksGrid';
import { linkTypeToName } from '../../link/types';
import { TableLoadError } from '../../TableLoadError/TableLoadError';
import { SettingsModalLayout } from '../SchemasV2/WorkspaceSettings/SettingsModalLayout';
import { AddLinkView, useEnabledLinkTypes } from './AddLinkView';

export const IntegrationsMainView = () => {
  const { context } = useCustomerApiContext();
  const [{ data, error, fetching }, reExecuteQuery] = useGetLinksQuery({
    context,
    pause: !context.token,
  });
  const { isError, result: links } = getResultOrError(data?.links, error);
  const canAddLink = useHasWorkspacePermission(Permissions.link.create);
  const linkTypes = useEnabledLinkTypes().filter((type) => type !== 'UnitLink');
  const refresh = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );
  const [addingLinkType, setAddingLinkType] = useState<LinkType | undefined>(
    undefined
  );
  const [sortMode, setSortMode] = useState<'desc' | 'asc'>('desc');
  const sortedLinks = isError
    ? []
    : [...links.nodes].sort((left, right) =>
        sortMode === 'desc'
          ? right.created.localeCompare(left.created)
          : left.created.localeCompare(right.created)
      );
  useDocumentTitle('Links');
  const closeAddLink = useCallback(() => setAddingLinkType(undefined), []);
  const onCreate = useCallback(() => {
    refresh();
    closeAddLink();
  }, [refresh, closeAddLink]);

  if (addingLinkType) {
    return (
      <SettingsModalLayout
        title={`Add ${linkTypeToName[addingLinkType]} Link`}
        onBackButtonClick={closeAddLink}
      >
        <AddLinkView linkType={addingLinkType} onCreate={onCreate} />
      </SettingsModalLayout>
    );
  }

  return (
    <SettingsModalLayout
      title="Integrations"
      topButtonSection={
        canAddLink && !isError ? (
          <Menu
            items={linkTypes.map((type) => ({
              key: type,
              display: (
                <span className="flex items-center gap-x-[1ch]">
                  <LinkIcon type={type} />
                  <span>{linkTypeToName[type]}</span>
                </span>
              ),
              onClick: () => setAddingLinkType(type),
            }))}
            justify="justify-end"
            dropdownOffset="right-0"
            dataTestId="add-link-button"
          >
            Add<span className="text-main-500">+</span>
          </Menu>
        ) : null
      }
    >
      {isError ? (
        <TableLoadError
          entityColumnName="Link"
          rightColumnName="ID"
          {...{ error, retry: refresh, fetching }}
        />
      ) : (
        <div className="flex flex-col min-h-f0 w-full space-y-fhalf">
          <TextButton
            extraClassNames="w-fit"
            onClick={() => setSortMode(sortMode === 'desc' ? 'asc' : 'desc')}
          >
            Most Recent {sortMode === 'desc' ? '↓' : '↑'}
          </TextButton>
          <LinksGrid links={sortedLinks} />
        </div>
      )}
    </SettingsModalLayout>
  );
};
